import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useState } from "react";
import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import {
  getPlatformUserMemberships,
  getPlatformUserMembershipsCancellationReasons,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

import { type MembershipCancellationLocationState } from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  const membershipCancellationReasons = queryClient.fetchQuery({
    ...getPlatformUserMembershipsCancellationReasons.keyed(),
  });

  return waitObj({
    platformUserMembershipId,
    userMemberships,
    membershipCancellationReasons,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

/**
 * First step of cancelling: the exit survey. The chosen reason travels in router state through
 * the save offers to the final confirm, which submits it with the cancellation.
 */
export const WalletMembershipCancelPage = () => {
  const { t } = useTranslation();
  const {
    platformUserMembershipId,
    userMemberships: initialUserMemberships,
    membershipCancellationReasons,
  } = useLoaderData() as LoaderData;

  const navigate = useNavigate();

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: initialUserMemberships,
  });

  const membership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  const [selectedCancellationReasonId, setSelectedCancellationReasonId] = useState<string>();

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipCancelPage.header")}
        </div>
      </TopNav>
      <div className="flex grow flex-col gap-y-6 bg-white px-6 pb-6 pt-3 text-left">
        <div className="flex flex-col gap-y-1">
          <h1 className="text-title">{t("walletMembershipCancelPage.reasonTitle")}</h1>
          <span className="text-body2 text-secondary">
            {t("walletMembershipCancelPage.reasonSubtitle", {
              membershipName: membership?.membership.name,
            })}
          </span>
        </div>
        <div
          role="radiogroup"
          aria-label={t("walletMembershipCancelPage.cancellationReasonLabel")}
          className="flex flex-col gap-y-2"
        >
          {membershipCancellationReasons.map(({ id, reason }) => {
            const isSelected = id === selectedCancellationReasonId;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelectedCancellationReasonId(id)}
                className={cx(
                  "flex items-center gap-x-3 rounded-xl border px-4 py-3 text-left text-body2",
                  isSelected
                    ? "border-brand-color bg-brand-color/10 text-primary"
                    : "border-black/10 text-primary",
                )}
              >
                <span
                  className={cx(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                    isSelected ? "border-brand-color" : "border-black/20",
                  )}
                  aria-hidden
                >
                  {isSelected && <span className="h-2.5 w-2.5 rounded-full bg-brand-color" />}
                </span>
                {reason}
              </button>
            );
          })}
        </div>
        <div className="grow" />
        <div className="flex flex-col gap-y-3">
          <Button
            fullWidth
            style="dangerSolid"
            disabled={!selectedCancellationReasonId}
            onClick={() =>
              navigate(`/wallet/memberships/manage/${platformUserMembershipId}/cancel-offers`, {
                state: {
                  membershipCancellationReasonId: selectedCancellationReasonId,
                } satisfies MembershipCancellationLocationState,
              })
            }
          >
            {t("walletMembershipCancelPage.continueToCancel")}
          </Button>
          <Button
            fullWidth
            style="secondary"
            onClick={() => navigate("/wallet/memberships/manage")}
          >
            {t("walletMembershipCancelPage.keepMembershipButton")}
          </Button>
        </div>
      </div>
    </div>
  );
};
